import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import {
  Button,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from '@material-ui/core';
import ControlPointIcon from '@material-ui/icons/ControlPoint';
import PreviewIcon from '@material-ui/icons/RemoveRedEyeSharp';
// import EditIcon from '@material-ui/icons/Edit';
// import DeleteIcon from '@material-ui/icons/Delete';
import { orange } from '@material-ui/core/colors';
import axios from 'server';
import { IdpFormData, IdpRecord, idpDefaultValues } from './types';
import IconButton from '@material-ui/core/IconButton';
import { useStore } from 'hooks/useRootStore';
import { useAppPreloader } from 'hooks/useAppPreloader';
const ThirdPartyTable: FC = () => {
  const {
    notificationStore: { openNotification },
  } = useStore();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IdpFormData>({ defaultValues: idpDefaultValues });

  const {
    data: apiResponse,
    isLoading: isFetchingIdentity,
    isError: isIdentityError,
    refetch,
  } = useQuery(
    'saml-identity-providers',
    async () => {
      const response = await axios.get('saml/identity-providers');
      return response;
    },
    { refetchOnWindowFocus: false }
  );

  const getIdentityList = (): IdpRecord[] => {
    if (Array.isArray(apiResponse)) return apiResponse;
    if (Array.isArray(apiResponse?.data)) return apiResponse.data;
    if (Array.isArray(apiResponse?.data?.data)) return apiResponse.data.data;
    if (Array.isArray(apiResponse?.data?.items)) return apiResponse.data.items;
    return [];
  };

  const identityProviders = getIdentityList();
  const paginatedData = identityProviders.slice(page * perPage, page * perPage + perPage);

  const createIdp = useMutation((data: IdpFormData) => axios.post('saml/identity-providers', data), {
    onSuccess: () => {
      openNotification('success', 'Identity Provider added successfully');
      setOpen(false);
      reset(idpDefaultValues);
      refetch();
    },
    onError: (err: any) => {
      openNotification('error', err?.response?.data?.message || 'Something went wrong');
    },
  });

  const handleClose = () => {
    setOpen(false);
    reset(idpDefaultValues);
    createIdp.reset();
  };

  const onSubmit = (data: IdpFormData) => {
    createIdp.mutate({
      ...data,
      slo_url: data.slo_url || (null as any),
    });
  };

  const [selectedIdp, setSelectedIdp] = useState<IdpRecord | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const handleView = (idp: IdpRecord) => {
    setSelectedIdp(idp);
    setViewOpen(true);
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setSelectedIdp(null);
  };
  useAppPreloader(isFetchingIdentity || createIdp.isLoading);
  return (
    <>
      <Grid container justify='flex-end' style={{ marginBottom: 16 }}>
        <Grid item>
          <Button
            variant='contained'
            color='secondary'
            style={{ backgroundColor: '#5081FF' }}
            onClick={() => setOpen(true)}
          >
            <ControlPointIcon />
          </Button>
        </Grid>
      </Grid>

      {isFetchingIdentity ? (
        <></>
      ) : isIdentityError ? (
        <Typography color='error' style={{ padding: 20 }}>
          Failed to load Third Party Providers.
        </Typography>
      ) : identityProviders.length === 0 ? (
        <Typography style={{ padding: 20, textAlign: 'center' }}>No Third Party Providers found.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Provider</strong>
                </TableCell>
                <TableCell>
                  <strong>Entity ID</strong>
                </TableCell>
                <TableCell align='right'>
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((idp) => (
                <TableRow key={idp.id} hover>
                  <TableCell>
                    <Typography style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>
                      {idp.name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      style={{ color: '#6b7280', fontSize: 13, wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                    >
                      {idp.entity_id || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                      <IconButton size='small' onClick={() => handleView(idp)}>
                        <PreviewIcon style={{ color: orange[500] }} />
                      </IconButton>{' '}
                      {/* <IconButton size='small'><EditIcon style={{ color: orange[500] }} /></IconButton> */}
                      {/* <IconButton size='small'><DeleteIcon style={{ color: red[500] }} /></IconButton> */}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {identityProviders.length > 0 && (
        <TablePagination
          component='div'
          count={identityProviders.length}
          page={page}
          onChangePage={(_: any, newPage: number) => setPage(newPage)}
          rowsPerPage={perPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onChangeRowsPerPage={(e: React.ChangeEvent<HTMLInputElement>) => {
            setPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          style={{ marginTop: 8 }}
        />
      )}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
        <DialogTitle>Add Identity Provider</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label='Name'
                  fullWidth
                  variant='outlined'
                  {...register('name', { required: 'Name is required' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='Entity ID'
                  fullWidth
                  variant='outlined'
                  {...register('entity_id', { required: 'Entity ID is required' })}
                  error={!!errors.entity_id}
                  helperText={errors.entity_id?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='SSO URL'
                  fullWidth
                  variant='outlined'
                  {...register('sso_url', { required: 'SSO URL is required' })}
                  error={!!errors.sso_url}
                  helperText={errors.sso_url?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField label='SLO URL' fullWidth variant='outlined' {...register('slo_url')} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='X509 Certificate'
                  fullWidth
                  multiline
                  rows={5}
                  variant='outlined'
                  {...register('x509_certificate', { required: 'Certificate is required' })}
                  error={!!errors.x509_certificate}
                  helperText={errors.x509_certificate?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='Email Attribute'
                  fullWidth
                  variant='outlined'
                  {...register('email_attribute', { required: 'Email attribute is required' })}
                  error={!!errors.email_attribute}
                  helperText={errors.email_attribute?.message}
                />
              </Grid>
            </Grid>
            {createIdp.isError && (
              <Typography style={{ color: 'red', marginTop: 10 }}>
                {(createIdp.error as any)?.response?.data?.message || 'Something went wrong'}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={createIdp.isLoading}>
              Cancel
            </Button>
            <Button type='submit' variant='contained' color='primary' disabled={createIdp.isLoading}>
              {createIdp.isLoading ? <CircularProgress size={20} /> : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={viewOpen} onClose={handleViewClose} fullWidth maxWidth='md'>
        <DialogTitle>Identity Provider Details</DialogTitle>

        <DialogContent>
          {selectedIdp && (
            <Grid container spacing={2}>
              {/* Identity Provider */}
              <Grid item xs={12}>
                <Typography
                  variant='subtitle1'
                  style={{
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  Basic Details
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label='Name'
                  value={selectedIdp.name || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label='UUID'
                  value={selectedIdp.uuid || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label='Entity ID'
                  value={selectedIdp.entity_id || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label='SSO URL'
                  value={selectedIdp.sso_url || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label='SLO URL'
                  value={selectedIdp.slo_url || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label='Email Attribute'
                  value={selectedIdp.email_attribute || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label='Status'
                  value={selectedIdp.is_active ? 'Active' : 'Inactive'}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label='Certificate Fingerprint SHA-256'
                  value={selectedIdp.certificate_fingerprint_sha256 || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{
                    readOnly: true,
                    style: {
                      fontFamily: 'monospace',
                      fontSize: 13,
                    },
                  }}
                />
              </Grid>

              {/* Service Provider */}
              <Grid item xs={12} style={{ marginTop: 8 }}>
                <Typography
                  variant='subtitle1'
                  style={{
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  Service Provider Details
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label='Entity ID'
                  value={selectedIdp.sp?.entity_id || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label='Metadata URL'
                  value={selectedIdp.sp?.metadata_url || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label='ACS URL'
                  value={selectedIdp.sp?.acs_url || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label='Login URL'
                  value={selectedIdp.sp?.login_url || ''}
                  fullWidth
                  variant='outlined'
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleViewClose} color='primary' variant='contained'>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ThirdPartyTable;
